import { ApolloQueryResult } from 'apollo-client';

import { mapCampaignFilterToApiFormat, mapRowToCampaign } from '../../mappers/campaign';
import {
    getValueFromApiFormat,
    mapAdditionalFilterToApiFormat,
    mapCampaignsCalcTypeFilterToApiFormat,
    mapSortParamsToString,
    mapTotalStatsToApiFormat
} from '../../mappers/common/common';
import { getDefaultSort } from '../../service/GridHelpers/GridHelpers';
import { ClientInfo } from '../../typings/common';
import { CampaignsQuery, CampaignsSuggestQuery, GdStatPresetInput } from '../../typings/graphql-grid';
import { defaultClientInfo, GridParams } from '../../typings/grid';
import { ClientFeatures } from '../../typings/state';

import { mapGridParamsToStatRequirements } from './common';
import { EntitiesQueryResponse } from './EntitiesDataProvider';

export function mapToCampaignDefaultApiInput(params: GridParams) {
    return {
        filter: {
            campaignIdIn: params.campaignsIds
        },
        statRequirements: {
            preset: GdStatPresetInput.TODAY
        },
        // Поумолчанию сортировка ставится по id ASC
        orderBy: [],
        // Поумолчанию лимит ставится 100
        limitOffset: {
            offset: 0,
            limit: 10
        }
    };
}

export const mapSourceParamsToCampaignAndSuggestQueryVariables = (
    params: GridParams,
    clientInfo: ClientInfo,
    features: ClientFeatures
) => {
    const {
        sortParams,
        campaignsIds,
        filter,
        additionalFilter,
        campaignsCalcTypeFilter,
        view,
        isOnlyFavoriteCampaignsVisible
    } = params;

    const apiFormatFilter = filter
        ? mapCampaignFilterToApiFormat(filter, clientInfo, features, campaignsCalcTypeFilter)
        : {};

    if (additionalFilter !== undefined) {
        apiFormatFilter.archived = mapAdditionalFilterToApiFormat(additionalFilter);
    }

    if (campaignsCalcTypeFilter !== undefined) {
        const calcType = mapCampaignsCalcTypeFilterToApiFormat(campaignsCalcTypeFilter);

        if (calcType) {
            apiFormatFilter.calcType = calcType;
        }
    }

    if (isOnlyFavoriteCampaignsVisible) {
        apiFormatFilter.favorite = isOnlyFavoriteCampaignsVisible;
    }

    return {
        orderBy: sortParams ? mapSortParamsToString(sortParams) : getDefaultSort(view),
        statRequirements: mapGridParamsToStatRequirements(params),
        filter:
            filter || additionalFilter || campaignsCalcTypeFilter
                ? {
                      ...apiFormatFilter,
                      campaignIdIn: campaignsIds || apiFormatFilter.campaignIdIn
                  }
                : {}
    };
};

export const mapCampaignQueryResultToEntitiesQueryResponse = (
    result: ApolloQueryResult<CampaignsQuery>,
    params: GridParams
): EntitiesQueryResponse => {
    const { client, reqId } = result.data;
    const {
        campaigns: campaignsResult,
        features,
        info: { workCurrency, countryRegionId }
    } = client;
    const { rowset: campaignsRows, totalCount, campaignIds, cacheKey, totalStats } = campaignsResult;

    const { cost, clicks, shows, ctr } = totalStats;

    return {
        clientInfo: {
            ...defaultClientInfo,
            ...features,
            workCurrency,
            countryRegionId: String(countryRegionId)
        },
        data: {
            data: campaignsRows.map(item => mapRowToCampaign(item, client, params)),
            cacheKey: getValueFromApiFormat(cacheKey),
            total: totalCount,
            allItemsIds: campaignIds.map(String)
        },
        totalStats: mapTotalStatsToApiFormat({
            cost,
            clicks,
            shows,
            ctr
        }),
        hasObjectsOverLimit: false,
        reqId
    };
};

export const mapCampaignSuggestQueryResultToSuggestQueryResponse = (
    result: ApolloQueryResult<CampaignsSuggestQuery>
) => {
    return {
        data: result.data.client.campaigns.rowset.map(item => item.name),
        reqId: result.data.reqId
    };
};
